import React, { useEffect, useState } from 'react';
import { Settings as SettingsApi } from '../lib/api';
import { Card, CardHeader, CardContent, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { HandCoins, Plus, Trash2, Copy, Loader2, QrCode } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { SupportQrDialog } from './SupportQrDialog';
import ImageDropzone from './ImageDropzone';

export const SupportSettings = ({ settings, onSaved }) => {
  const [tiers, setTiers] = useState([]);
  const [viewEnabled, setViewEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [qrTarget, setQrTarget] = useState(null);
  const [heroImage, setHeroImage] = useState('');

  useEffect(() => {
    if (settings) {
      setTiers((settings.supportTiers || []).map(t => ({ ...t })));
      setViewEnabled(settings.contributionsViewEnabled !== false);
      setHeroImage(settings.supportHeroImage || '');
    }
  }, [settings]);

  const setTier = (i, patch) => setTiers(prev => prev.map((t, idx) => idx === i ? { ...t, ...patch } : t));
  const addTier = (audience) => setTiers(prev => [...prev, { id: `tier-${Date.now()}`, name: '', amount: 50, description: '', audience }]);
  const removeTier = (i) => setTiers(prev => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    const clean = tiers
      .map(t => ({ ...t, name: (t.name || '').trim(), amount: Number(t.amount) || 0, description: (t.description || '').trim() }))
      .filter(t => t.name && t.amount >= 0);
    setSaving(true);
    try {
      await SettingsApi.update({ supportTiers: clean, contributionsViewEnabled: viewEnabled, supportHeroImage: heroImage });
      toast({ title: 'Support tiers saved', description: 'Levels synced to Stripe.' });
      onSaved?.();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || String(e) });
    } finally {
      setSaving(false);
    }
  };

  const copyLink = (path) => {
    navigator.clipboard.writeText(`${window.location.origin}${path}`);
    toast({ title: 'Link copied', description: `${window.location.origin}${path}` });
  };

  const PAGES = [
    { path: '/support', label: 'Combined page', desc: 'Both audiences side by side' },
    { path: '/support/listeners', label: 'Listeners & Supporters page', desc: 'Send to fans and followers' },
    { path: '/support/guests', label: 'Guests & PR Firms page', desc: 'Send to past guests, publicists and PR teams' },
  ];

  return (
    <Card className="border-stone-200 mt-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-[15px] flex items-center gap-2"><HandCoins className="w-4 h-4" /> Support & contributions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-[12.5px] text-stone-600">
          Each audience has its own page — send the right link to the right people.
          Merge tags for emails: <code className="bg-stone-100 px-1 rounded">{'{{supportLink}}'}</code> (combined), <code className="bg-stone-100 px-1 rounded">{'{{listenersSupportLink}}'}</code>, <code className="bg-stone-100 px-1 rounded">{'{{guestsSupportLink}}'}</code>.
        </div>
        <div className="space-y-1.5">
          {PAGES.map(p => (
            <div key={p.path} className="flex items-center gap-2 p-2 rounded-md border border-stone-200" data-testid={`support-page-row-${p.path.split('/').pop() || 'combined'}`}>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-stone-900">{p.label}</div>
                <div className="text-[11px] text-stone-500 truncate">{p.desc} · {window.location.origin}{p.path}</div>
              </div>
              <Button size="sm" variant="outline" data-testid={`copy-link-${p.path.split('/').pop() || 'combined'}`} onClick={() => copyLink(p.path)}><Copy className="w-3.5 h-3.5 mr-1.5" /> Copy link</Button>
              <Button size="sm" variant="outline" data-testid={`qr-btn-${p.path.split('/').pop() || 'combined'}`} onClick={() => setQrTarget(p)}><QrCode className="w-3.5 h-3.5 mr-1.5" /> QR</Button>
              <a href={p.path} target="_blank" rel="noreferrer" className="text-[12px] text-stone-500 underline underline-offset-2 shrink-0">Preview</a>
            </div>
          ))}
        </div>
        <SupportQrDialog open={!!qrTarget} onOpenChange={(o) => !o && setQrTarget(null)} path={qrTarget?.path || '/support'} label={qrTarget?.label || 'Support page'} />

        <div className="max-w-md">
          <Label className="text-[11px] uppercase text-stone-500">Support page hero image <span className="normal-case">(wide format works best — leave empty for the default studio scene)</span></Label>
          <div className="mt-1" data-testid="support-hero-dropzone">
            <ImageDropzone value={heroImage} onChange={setHeroImage} label="Drop a hero image or click to upload" aspect="video" />
          </div>
        </div>

        <div className="space-y-4">
          {[
            { key: 'listeners', title: 'Listeners & Supporters' },
            { key: 'guests', title: 'Guests & PR Firms' },
          ].map(group => (
            <div key={group.key} className="space-y-3">
              <div className="text-[12px] font-semibold uppercase tracking-wide text-stone-500">{group.title}</div>
              {tiers.map((t, i) => ((t.audience || 'listeners') !== group.key) ? null : (
                <div key={t.id} data-testid={`support-tier-editor-${i}`} className="p-3 rounded-md border border-stone-200 space-y-2">
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Label className="text-[11px] uppercase text-stone-500">Level name</Label>
                      <Input data-testid={`tier-name-${i}`} value={t.name} onChange={(e) => setTier(i, { name: e.target.value })} placeholder="e.g. Patron" />
                    </div>
                    <div className="w-32">
                      <Label className="text-[11px] uppercase text-stone-500">Amount ($, 0 = free)</Label>
                      <Input data-testid={`tier-amount-${i}`} type="number" min="0" value={t.amount} onChange={(e) => setTier(i, { amount: e.target.value })} />
                    </div>
                    <div className="w-32">
                      <Label className="text-[11px] uppercase text-stone-500">Billing</Label>
                      <select
                        data-testid={`tier-interval-${i}`}
                        value={t.interval === 'month' ? 'month' : 'one_time'}
                        onChange={(e) => setTier(i, { interval: e.target.value })}
                        className="h-10 w-full rounded-md border border-stone-200 bg-white px-2 text-[13px]"
                      >
                        <option value="one_time">One-time</option>
                        <option value="month">Monthly</option>
                      </select>
                    </div>
                    <button data-testid={`tier-remove-${i}`} onClick={() => removeTier(i)} className="self-end mb-2 text-stone-400 hover:text-red-600" title="Remove tier">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase text-stone-500">Benefits description</Label>
                    <Textarea
                      data-testid={`tier-description-${i}`}
                      value={t.description}
                      onChange={(e) => setTier(i, { description: e.target.value })}
                      placeholder="What this level gets — announced on the support page"
                      rows={Math.max(3, Math.ceil((t.description || '').length / 85) + (t.description || '').split('\n').length - 1)}
                      className="text-[13px] leading-relaxed resize-y"
                    />
                  </div>
                </div>
              ))}
              <Button size="sm" variant="outline" data-testid={`add-tier-btn-${group.key}`} onClick={() => addTier(group.key)}><Plus className="w-3.5 h-3.5 mr-1" /> Add {group.title} level</Button>
            </div>
          ))}
        </div>

        <label className="flex items-center gap-2.5 cursor-pointer">
          <input type="checkbox" data-testid="contributions-view-toggle" checked={viewEnabled} onChange={(e) => setViewEnabled(e.target.checked)} className="w-4 h-4 accent-stone-900" />
          <span className="text-[13px] text-stone-800">Show the Contributions list in the app <span className="text-stone-500">(donors appear by initials only)</span></span>
        </label>

        <div className="flex justify-end">
          <Button data-testid="save-support-settings" onClick={save} disabled={saving} className="bg-stone-900 text-white hover:bg-stone-800">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null} Save support settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
